import { IS_AWS_ENV, NOT_AVAILABLE } from "constants/app.constants";
import { ALL_CHART_TYPES } from "constants/chart.constants";
import _omit from "lodash/omit";

import {
  IChartSettingsState,
  initialChartSettingsState,
  initialPresetState
} from "components/multiphase-chart/context/ChartSettingsContext";
import { IChartPreset, IProduct } from "components/multiphase-chart/models/shared.models";
import { getDefaultProductStylesMergedWithCustomStyles } from "components/multiphase-chart/util/productStyles";
import type { State as XdaProviderState } from "components/vis/context/types";
import { createInitialState } from "components/vis/context/xdaSettingsStore";

/**
 * Every name the panel owns carries the `well-card-panel` prefix. `well-card`
 * alone is the existing Well Ticket widget key.
 */
export const WELL_CARD_PANEL_CHART_ID = "well-card-panel";
export const WELL_CARD_PANEL_CHART_TYPE = "well-card-panel-chart";
export const WELL_CARD_PANEL_XDA_ID = "well-card-panel";
export const WELL_CARD_PANEL_XDA_TYPE = "well-card-panel-xda";
/**
 * Which slot the card's 3D viewer keeps its options in for the session. It
 * reads the same as the screenshot identity below and means something else:
 * that one says who answers the camera, this one says where the settings are
 * written. Kept apart so a change made for one reason cannot move the other.
 */
export const WELL_CARD_PANEL_THREE_D_ID = "well-card-panel-three-d";
/**
 * DOM ids, not React keys: `XdaViewer` puts these straight on elements that
 * `document.getElementById` and `ReactTooltip` look up by id. A dashboard XDA
 * widget uses the component's own defaults, so the panel's instance must not.
 */
export const WELL_CARD_PANEL_XDA_SCREENSHOT_ID = "well-card-panel-xda-screenshot-overlay";
export const WELL_CARD_PANEL_XDA_TOOLTIP_ID = "well-card-panel-xda-tooltip";
export const WELL_CARD_PANEL_THREE_D_SCREENSHOT_ID =
  "well-card-panel-three-d-screenshot-overlay";

/**
 * Who the card's two viewers are when their cameras are pressed.
 *
 * A dashboard widget announces the viewer's KIND ("xda", "ml"), so both it and
 * the card's viewer answered the same name: pressing either camera put both
 * into screenshot mode and left the capture with two candidates. These are the
 * card's own, in the same shape the chart already had - its screenshot is
 * keyed on the chart's id, which is `WELL_CARD_PANEL_CHART_ID`.
 */
export const WELL_CARD_PANEL_XDA_WIDGET_ID = "well-card-panel-xda";
export const WELL_CARD_PANEL_THREE_D_WIDGET_ID = "well-card-panel-three-d";

// ── Field ids ────────────────────────────────────────────────────────────────
// Hand-picked, not derived from group membership. Frozen as module constants so
// the query key stays stable across renders.

/**
 * Which control pointed the well card at its well. Sent with the view event so
 * opening the card from the map reads differently from retargeting an open one.
 */
export const WELL_CARD_VIEW_SOURCE = {
  previewPopup: "map-popover",
  wellList: "well-list",
  jumpToLocation: "jump-to-location",
  quickWellJump: "quick-well-jump",
  wellListInput: "well-list-input"
} as const;

export type WellCardViewSource =
  (typeof WELL_CARD_VIEW_SOURCE)[keyof typeof WELL_CARD_VIEW_SOURCE];

/**
 * The first name a source actually reported. A field the service has no value
 * for answers with the text "n/a" rather than with nothing, so the pick is made
 * on the value. The casing is the service's, so it is not relied on.
 */
export function reportedName(...values: unknown[]): Maybe<string> {
  return values.find(
    (value): value is string =>
      typeof value === "string" &&
      value.trim() !== "" &&
      value.trim().toLowerCase() !== NOT_AVAILABLE
  );
}

export const HEADER_FIELDS = [
  "Header.EntityName",
  "Header.WellName",
  "Header.Operator_Short",
  "Header.WellType",
  "Header.Status",
  "Header.ResourcePlay",
  "Header.ResourceType",
  "Dates.Vintage_YearMonth",
  "Geology.Zone"
] as const;

export interface PanelFieldSpec {
  readonly property: string;
  /** Rendered when the catalogue has no entry for the property. */
  readonly fallbackLabel: string;
  readonly boolean?: boolean;
  /** Rounded to a whole number before it is rendered. */
  readonly integer?: boolean;
  /** Shown in place of the catalogue title. The catalogue's unit is kept. */
  readonly label?: string;
  /** Text for true and false in place of Y / N. Needs `boolean`. */
  readonly booleanText?: readonly [string, string];
}

const AZURE_WELL_PARAMETER_FIELDS: readonly PanelFieldSpec[] = [
  { property: "Well_Design.WellboreDirection", fallbackLabel: "Wellbore Direction" },
  { property: "Geology.Bench", fallbackLabel: "Bench" },
  { property: "Well_Design.TVDKB_m", fallbackLabel: "TVD KB" },
  { property: "Well_Design.MD_m", fallbackLabel: "MD" },
  {
    property: "TuringParentChild.IsChildwithinPlay",
    fallbackLabel: "Is Child within Play",
    boolean: true
  },
  { property: "Well_Design.HzLength_m", fallbackLabel: "Hz Length" },
  { property: "Frac_Data.ProppantIntensity_tm", fallbackLabel: "Proppant Intensity" },
  { property: "Frac_Data.FluidIntensity_m3m", fallbackLabel: "Fluid Intensity" },
  { property: "Frac_Data.StageSpacing_m", fallbackLabel: "Stage Spacing" },
  {
    property: "Well_Design.AvgLateralSpacingtoClosestWell_m",
    fallbackLabel: "Lateral Spacing to Closest Well in Play"
  }
];

const AZURE_SPACING_METRIC_FIELDS: readonly PanelFieldSpec[] = [
  {
    property: "Well_Design.AvgLateralSpacingtoClosestWell_m",
    fallbackLabel: "Lateral Spacing to Closest Well in Play"
  },
  { property: "Well_Design.WellCount", fallbackLabel: "Well Count in Play" },
  {
    property: "TuringParentChild.IsParentwithinPlay",
    fallbackLabel: "Is Parent within Play",
    boolean: true
  },
  {
    property: "TuringParentChild.IsChildwithinPlay",
    fallbackLabel: "Is Child within Play",
    boolean: true
  },
  {
    property: "TuringParentChild.NumberOfParentWellswithinPlay",
    fallbackLabel: "# of Parent Wells within Play",
    integer: true
  },
  {
    property: "TuringParentChild.NumberOfChildrenWellswithinPlay",
    fallbackLabel: "# of Child Wells within Play",
    integer: true
  },
  {
    property: "TuringParentChild.ParentCumHoursDWwithinPlay",
    fallbackLabel: "Distance Weighted Parent Cum Hours within Play"
  }
];

// A boolean-looking value reads as Child / Parent. Anything else shows as it
// arrives.
const AWS_PARENT_CHILD_FIELD: PanelFieldSpec = {
  property: "Well_Spacing_Summary.ParentChild",
  fallbackLabel: "Parent/Child",
  boolean: true,
  booleanText: ["Child", "Parent"]
};

const AWS_WELL_PARAMETER_FIELDS: readonly PanelFieldSpec[] = [
  { property: "Header.Direction", fallbackLabel: "Direction" },
  { property: "Geology.Zone", fallbackLabel: "Zone" },
  { property: "Well_Design.TVDKB_m", fallbackLabel: "TVD KB" },
  { property: "Well_Design.MD_m", fallbackLabel: "MD" },
  AWS_PARENT_CHILD_FIELD,
  { property: "Well_Design.HzLength_m", fallbackLabel: "Hz Length" },
  { property: "Frac_Data.ProppantIntensity_tm", fallbackLabel: "Proppant Intensity" },
  { property: "Frac_Data.FluidIntensity_m3m", fallbackLabel: "Fluid Intensity" },
  { property: "Frac_Data.StageSpacing_m", fallbackLabel: "Stage Spacing" },
  {
    property: "Well_Spacing_Summary.ClosestWellXy",
    fallbackLabel: "Closest XY",
    label: "Closest XY"
  }
];

// Read row by row in a two-column grid, left cell first.
// The labels are the Insights names, followed by the catalogue unit.
const AWS_SPACING_METRIC_FIELDS: readonly PanelFieldSpec[] = [
  {
    property: "Well_Spacing_Summary.ClosestWellXy",
    fallbackLabel: "Spacing Closest Well XY",
    label: "Spacing Closest Well XY"
  },
  {
    property: "Well_Spacing_Summary.WellsInRadius",
    fallbackLabel: "Wells In Radius",
    label: "Wells In Radius",
    integer: true
  },
  {
    property: "Well_Spacing_Summary.WellsInRadiusAvgXy",
    fallbackLabel: "Wells In Radius Avg XY",
    label: "Wells In Radius Avg XY"
  },
  {
    ...AWS_PARENT_CHILD_FIELD,
    fallbackLabel: "Spacing Is Child",
    label: "Spacing Is Child"
  },
  {
    property: "Well_Spacing_Summary.ParentCount",
    fallbackLabel: "Parent Count",
    label: "Parent Count",
    integer: true
  },
  {
    property: "Well_Spacing_Summary.ParentDaysOnline",
    fallbackLabel: "Parent Days Online",
    label: "Parent Days Online"
  },
  {
    property: "Well_Spacing_Summary.LateralCloserXy",
    fallbackLabel: "Lateral Closer XY",
    label: "Lateral Closer XY"
  },
  {
    property: "Well_Spacing_Summary.StaggerCloserTangent",
    fallbackLabel: "Stagger Closer Tangent",
    label: "Stagger Closer Tangent"
  },
  {
    property: "Well_Spacing_Summary.StackCloserZ",
    fallbackLabel: "Stack Closer Z",
    label: "Stack Closer Z",
    integer: true
  },
  {
    property: "Well_Spacing_Summary.BoundednessScore",
    fallbackLabel: "Boundedness Score",
    label: "Boundedness Score",
    integer: true
  }
];

export const WELL_PARAMETER_FIELDS = IS_AWS_ENV
  ? AWS_WELL_PARAMETER_FIELDS
  : AZURE_WELL_PARAMETER_FIELDS;
export const SPACING_METRIC_FIELDS = IS_AWS_ENV
  ? AWS_SPACING_METRIC_FIELDS
  : AZURE_SPACING_METRIC_FIELDS;
export const SPACING_METRIC_COLUMNS = IS_AWS_ENV ? 2 : 4;

export interface PerformanceProduct {
  readonly label: string;
  readonly swatch: string;
  /** Segment between `Performance.` and the metric id. */
  readonly key: string;
}

/**
 * The colours the chart plots its series in, read from the same helper
 * `Chart.tsx` builds its own styles with. The panel seeds a preset with no
 * custom series styles, so these are exactly the lines drawn above the table,
 * and a swatch cannot disagree with the legend beside it. A user's own custom
 * series colours are not followed — this list is a module constant, and the
 * mismatch it fixes was against the defaults.
 */
const chartProductStyles = getDefaultProductStylesMergedWithCustomStyles([]);

export const PERFORMANCE_PRODUCTS: readonly PerformanceProduct[] = [
  { label: "Oil", swatch: chartProductStyles.oil.color, key: "Oil" },
  { label: "Gas", swatch: chartProductStyles.gas.color, key: "Gas" },
  { label: "Water", swatch: chartProductStyles.water.color, key: "Water" },
  { label: "BOE (6:1)", swatch: chartProductStyles.boe.color, key: "BOE" }
];

export interface PerformanceMetric {
  readonly id: string;
  readonly fallbackLabel: string;
}

export const PERFORMANCE_METRICS: readonly PerformanceMetric[] = [
  { id: "PeakCalRate", fallbackLabel: "Peak Cal. Rate" },
  { id: "IP30CalRate", fallbackLabel: "IP 30 Cal. Rate" },
  { id: "IP90CalRate", fallbackLabel: "IP 90 Cal. Rate" },
  { id: "CumToDate", fallbackLabel: "Cum To Date" },
  { id: "EUR", fallbackLabel: "EUR" }
];

export const performanceFieldId = (product: string, metric: string): string =>
  `Performance.${product}.${metric}`;

export interface ProductionSummaryRow {
  readonly label: string;
  readonly swatch: string;
  /** One id per entry of `PRODUCTION_SUMMARY_COLUMNS`, in the same order. */
  readonly fields: readonly string[];
}

export const PRODUCTION_SUMMARY_COLUMNS: readonly string[] = [
  "Peak Month Volume",
  "12 months Cum",
  "24 months Cum",
  "Life Cum",
  "EUR50"
];

// Written out in full: the catalogue's casing is irregular ("Eur50YearOil" but
// "EUR50YearWater"), and a wrong id renders "-" with no error.
export const PRODUCTION_SUMMARY_ROWS: readonly ProductionSummaryRow[] = [
  {
    label: "Oil",
    swatch: chartProductStyles.oil.color,
    fields: [
      "Production_Summary.PeakMonthOilVolume",
      "Production_Summary.Cumulative12MonthsOil",
      "Production_Summary.Cumulative24MonthsOil",
      "Production_Summary.CumulativeLifeOil",
      "Production_Summary.Eur50YearOil"
    ]
  },
  {
    label: "Gas",
    swatch: chartProductStyles.gas.color,
    fields: [
      "Production_Summary.PeakMonthGasVolume",
      "Production_Summary.Cumulative12MonthsGas",
      "Production_Summary.Cumulative24MonthsGas",
      "Production_Summary.CumulativeLifeGas",
      "Production_Summary.Eur50YearGas"
    ]
  },
  {
    label: "BOE (6:1)",
    swatch: chartProductStyles.boe.color,
    fields: [
      "Production_Summary.PeakMonthBOEVolume6To1",
      "Production_Summary.Cumulative12MonthsBOE6To1",
      "Production_Summary.Cumulative24MonthsBOE6To1",
      "Production_Summary.CumulativeLifeBOE6To1",
      "Production_Summary.EUR50YearBOE6To1"
    ]
  },
  {
    label: "Water",
    swatch: chartProductStyles.water.color,
    fields: [
      "Production_Summary.PeakMonthWaterVolume",
      "Production_Summary.Cumulative12MonthsWater",
      "Production_Summary.Cumulative24MonthsWater",
      "Production_Summary.CumulativeLifeWater",
      "Production_Summary.EUR50YearWater"
    ]
  }
];

export interface LifecycleMilestone {
  readonly label: string;
  /** `null` where no EVA field exists yet. */
  readonly property: Maybe<string>;
  /** The label wins over the catalogue title. */
  readonly fixedLabel?: boolean;
}

const AZURE_LIFECYCLE_MILESTONES: readonly LifecycleMilestone[] = [
  { label: "Permit Submitted", property: null },
  { label: "Permit Approved Date", property: "Dates.PermitApprovedDate" },
  { label: "Spud Date", property: "Dates.SpudDate" },
  { label: "Final Drill Date", property: "Dates.FinalDrillDate" },
  { label: "Completion Date", property: "Dates.CompletionDate" },
  { label: "On Prod Date", property: "Dates.OnProdDate" },
  { label: "Abandonment Date", property: "Dates.AbandonmentDate" }
];

const AWS_LIFECYCLE_MILESTONES: readonly LifecycleMilestone[] = [
  { label: "Permit Submitted", property: "Dates.PermitSubmitDate", fixedLabel: true },
  { label: "Permit Approved", property: "Dates.PermitApprovedDate", fixedLabel: true },
  { label: "Spud", property: "Dates.SpudDate", fixedLabel: true },
  { label: "Drilling End", property: "Dates.FinalDrillDate", fixedLabel: true },
  { label: "Completion", property: "Dates.CompletionDate", fixedLabel: true },
  { label: "Initial Production", property: "Dates.OnProdDate", fixedLabel: true },
  { label: "Last Refrac", property: "Dates.LastRefracDate", fixedLabel: true },
  { label: "Plugged", property: "Dates.PluggedDate", fixedLabel: true }
];

export const LIFECYCLE_MILESTONES = IS_AWS_ENV
  ? AWS_LIFECYCLE_MILESTONES
  : AZURE_LIFECYCLE_MILESTONES;

/** The AWS data carries the state permit number as the license number. */
export const PERMIT_ID_FIELD = "Header.LicenseNumber";

/** Every id the panel's one data request carries. */
export const WELL_CARD_PANEL_FIELDS: readonly string[] = Array.from(
  new Set<string>([
    ...HEADER_FIELDS,
    ...WELL_PARAMETER_FIELDS.map((f) => f.property),
    ...SPACING_METRIC_FIELDS.map((f) => f.property),
    // The AWS card shows the Production Summary in the Performance table's place.
    ...(IS_AWS_ENV
      ? []
      : PERFORMANCE_PRODUCTS.flatMap((p) =>
          PERFORMANCE_METRICS.map((m) => performanceFieldId(p.key, m.id))
        )),
    ...LIFECYCLE_MILESTONES.map((m) => m.property).filter((p): p is string => !!p),
    ...(IS_AWS_ENV ? PRODUCTION_SUMMARY_ROWS.flatMap((r) => r.fields) : []),
    ...(IS_AWS_ENV ? [PERMIT_ID_FIELD] : [])
  ])
);

// ── Seeded chart state ───────────────────────────────────────────────────────

// Both fields are set because the provider takes the seed verbatim: only a mode
// switch or a preset load runs the reducer that derives one from the other, and
// the chart request sends the id.
const RATE_TIME = {
  chartType: "monthlyRateTime",
  chartTypeId: ALL_CHART_TYPES.RateTime.id
};
const RATE_DATE = { chartType: "rateDate", chartTypeId: ALL_CHART_TYPES.RateDate.id };

// Oil and Water on the left, Gas on the right. Sales Gas is not seeded: it is a
// source-dependent stream that most wells answer nothing for, and the chart's
// own product controls still add it.
const STREAM_PRODUCTS: IProduct[] = [
  { axis: "primary", name: "Oil + Cond" },
  { axis: "primary", name: "Water" },
  { axis: "secondary", name: "Gas" }
];

// Both ratios share the right axis, which takes at most two unit types.
const ratioProducts = (boe: string): IProduct[] => [
  { axis: "primary", name: boe },
  { axis: "secondary", name: "GOR" },
  { axis: "secondary", name: "WOR" }
];

// A non-empty id is what marks a preset as seeded rather than blank.
const seededPreset = (
  id: string,
  name: string,
  chart: typeof RATE_TIME,
  products: IProduct[]
): IChartPreset => ({
  ...initialPresetState,
  id: `well-card-panel-${id}`,
  name,
  ...chart,
  products
});

// Rate against producing time, which the dashboard widget's default of rate
// against cum does not give.
const wellCardPreset = seededPreset(
  "default",
  "Rate vs Time - Oil, Gas, Water",
  RATE_TIME,
  STREAM_PRODUCTS
);

// Grouped by chart type so the leading Rate vs Time/Date reads as two blocks.
const WELL_CARD_PRESETS: IChartPreset[] = [
  wellCardPreset,
  seededPreset(
    "boe6-time",
    "Rate vs Time - BOE (6:1), GOR, WOR",
    RATE_TIME,
    ratioProducts("BOE")
  ),
  seededPreset(
    "boe20-time",
    "Rate vs Time - BOE (20:1), GOR, WOR",
    RATE_TIME,
    ratioProducts("BOE 20")
  ),
  seededPreset("rate-date", "Rate vs Date - Oil, Gas, Water", RATE_DATE, STREAM_PRODUCTS),
  seededPreset(
    "boe6-date",
    "Rate vs Date - BOE (6:1), GOR, WOR",
    RATE_DATE,
    ratioProducts("BOE")
  ),
  seededPreset(
    "boe20-date",
    "Rate vs Date - BOE (20:1), GOR, WOR",
    RATE_DATE,
    ratioProducts("BOE 20")
  )
];

/**
 * Seeded whole, not partially: the provider replaces the entire object and
 * silently discards a seed with no `settings`. Both presets are seeded because
 * the request is built from `storedPreset` while the settings UI edits
 * `currentPreset`.
 */
export const WELL_CARD_CHART_STATE: IChartSettingsState = {
  ...initialChartSettingsState,
  /*
   * The shared initial state carries `debugMode` and `backfit` ON, so every
   * chart instance starts with both. The card opens with them off: backfit
   * draws a second pass over the history the card is there to show, and the
   * debug options are a developer's tool that only renders in a dev build
   * anyway.
   *
   * Spread rather than replaced, so every other setting is still the chart's
   * own default, and a user can turn either back on inside the panel. Nothing
   * here reaches a dashboard widget, which reads `initialChartSettingsState`
   * directly.
   */
  settings: {
    ...initialChartSettingsState.settings,
    debugMode: false,
    backfit: false
  },
  currentPreset: wellCardPreset,
  storedPreset: wellCardPreset,
  // No server holds these, so without them here the preset list has no way
  // back to the card's own charts once another preset is picked.
  builtInPresets: WELL_CARD_PRESETS,
  hiddenToolbarKeys: ["Popout", "FullscreenToggle"],
  skipInitialPresetLoad: true
};

// ── Seeded XDA settings ──────────────────────────────────────────────────────

/**
 * The two source fields are dropped from the seed, everything else is seeded
 * whole. The provider reads a seeded `topsModelSource` as a dashboard's saved
 * choice and then skips the effect that applies the user's own surface source,
 * so a seeded factory value pinned the card to McDaniel while a fresh viewer
 * followed the user's setting. Absent is the case the provider fills from the
 * user defaults, both at mount and again when the settings request answers.
 * `ipdbSource` goes with it because that same effect writes both together.
 */
const wellCardXdaSettings = _omit(
  createInitialState({
    // The on-plot well labels start off. The fields below are still the ones
    // the design asks for, so turning Show Data on gives them straight away
    // rather than an empty set to configure.
    showData: false,
    dataFields: [
      { property: "Geology.Zone", title: "Zone" },
      { property: "Dates.Vintage_YearMonth", title: "Vintage (Year-Month)" },
      { property: "Header.ResourcePlay", title: "Resource Play" }
    ]
  }),
  ["topsModelSource", "ipdbSource"]
);

/**
 * Seeded whole, because the provider uses the seed verbatim rather than merging
 * it with defaults. The type assertion covers the two omitted source fields,
 * which the settings type marks required and the provider reads with `??`.
 */
export const WELL_CARD_XDA_STATE = {
  xda: {
    settings: wellCardXdaSettings
  }
} as XdaProviderState;
